import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApolloClient, useMutation } from '@apollo/client';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, VStack, Divider, Button } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useForm } from 'react-hook-form';
import { formatPhoneNumber } from 'react-phone-number-input';

import { CollectVerifyUnifiedPhone } from './components/CollectVerifyUnifiedPhone';
import { TUnifiedUserFormData, UnifiedUserFormSchema } from './schema';
import {
  CollectPhone,
  CollectPhoneStatus,
} from '../../../../../artifacts/gql/graphql';
import { createFormElements } from '../../../../Form';
import { resolvePhoneConflict } from '../../../../gql/mutations/resolvePhoneConflict';
import { updateProfileName } from '../../../../gql/mutations/updateProfileName';
import { updateUserPhone } from '../../../../gql/mutations/updateUserPhone';
import { useUnifiedUserData } from '../../../../hooks/useUnifiedUserData';
import { useUnifiedUserStatus } from '../../../../hooks/useUnifiedUserStatus';
import { EButtonVariant, EColor } from '../../../../Theme';
import { tracker } from '../../../../utilities/tracker';
import { ModalContent } from '../../../components';
import { useModalStore, useProfileStore } from '../../../stores';
import { EModalContent } from '../../../types';

const { Form, FormTextInput, FormFloatingPhoneInput } =
  createFormElements<TUnifiedUserFormData>();

export const UnifiedUserPhoneVerificationContainer = () => {
  const modalStore = useModalStore();
  const { updateProfile } = useProfileStore();
  const { unifiedUser } = useUnifiedUserData();
  const client = useApolloClient();
  const [error, setError] = useState<string>();
  const [showCollectVerifyUnifiedPhone, setShowCollectVerifyUnifiedPhone] =
    useState(false);
  const [phoneToVerify, setPhoneToVerify] = useState<string | undefined>();
  const [isRefetching, setIsRefetching] = useState(false);

  const modalData =
    modalStore.lookup<EModalContent.UNIFIED_USER_PHONE_VERIFICATION>();
  const issue = modalData?.issue;

  const [updateProfileNameMutation, { loading: isUpdateNameLoading }] =
    useMutation(updateProfileName);
  const [updateUserPhoneMutation, { loading: isUpdateUserPhoneLoading }] =
    useMutation(updateUserPhone);
  const [
    resolvePhoneConflictMutation,
    { loading: isResolvePhoneConflictLoading },
  ] = useMutation(resolvePhoneConflict);

  const isModalOpen = useMemo(
    () =>
      modalStore.lookup()?.name ===
      EModalContent.UNIFIED_USER_PHONE_VERIFICATION,
    [modalStore]
  );

  // Determine what sections to show based on unified user data
  const shouldShowNameSection = useMemo(() => {
    if (!unifiedUser) return false;
    const firstName = unifiedUser.firstName?.trim();
    const lastName = unifiedUser.lastName?.trim();
    return !firstName || !lastName;
  }, [unifiedUser]);

  const shouldShowPhoneSection = useMemo(() => {
    if (!unifiedUser) return false;
    const hasPhoneConflict =
      unifiedUser.unifiedPhoneStatus?.hasConflict || false;
    return hasPhoneConflict;
  }, [unifiedUser]);

  const isLoading =
    isUpdateNameLoading ||
    isUpdateUserPhoneLoading ||
    isResolvePhoneConflictLoading ||
    isRefetching;

  const handleClose = useCallback(() => {
    setShowCollectVerifyUnifiedPhone(false);
    setError(undefined);
    modalStore.clear();
  }, [modalStore]);

  const handleSubmit = async (data: TUnifiedUserFormData) => {
    setError(undefined);

    try {
      // For now, just log the data and show success
      tracker.track('Unified User Debug - Form Submitted', {
        formData: data,
        issueType: issue?.type,
        hasNameData: !!(data.firstName && data.lastName),
        hasPhoneData: !!(data.selectedPhoneNumber || data.newPhoneNumber),
      });

      // Update name if needed
      if (data.firstName && data.lastName) {
        const { data: nameUpdateData } = await updateProfileNameMutation({
          variables: {
            firstName: data.firstName,
            lastName: data.lastName,
          },
        });

        if (!nameUpdateData?.collectUpdateProfileName?.successful) {
          const { messages } = nameUpdateData?.collectUpdateProfileName ?? {};
          if (messages?.length) {
            const errorMessage =
              messages[0]?.message ?? 'Failed to update name';
            setError(errorMessage);
            tracker.track('Unified User Name Update - Failed', {
              error: errorMessage,
            });
            return;
          }
        }

        // Update local user state
        updateProfile({
          firstName: data.firstName,
          lastName: data.lastName,
        });

        tracker.track('Unified User Name Update - Success', {
          firstName: data.firstName,
          lastName: data.lastName,
        });
      }
      // Determine which phone to verify and its status
      let phone: string = '';
      let isVerified = false;
      let isNewPhone = false;

      if (shouldShowPhoneSection) {
        if (data.selectedPhoneNumber && data.selectedPhoneNumber !== 'new') {
          phone = data.selectedPhoneNumber;
          // Find the phone in unifiedUser.unifiedPhoneStatus (livePhoneNumber/collectPhoneNumber)
          const allPhones: CollectPhone[] = [
            unifiedUser?.unifiedPhoneStatus?.livePhoneNumber,
            unifiedUser?.unifiedPhoneStatus?.collectPhoneNumber,
          ].filter(Boolean) as CollectPhone[];
          const matched = allPhones.find((p) => p?.number === phone);
          isVerified =
            !!matched?.status &&
            [
              CollectPhoneStatus.PhoneStatusVerified,
              CollectPhoneStatus.PhoneStatusManuallyVerified,
            ].includes(matched?.status);
        } else if (data.selectedPhoneNumber === 'new' && data.newPhoneNumber) {
          phone = data.newPhoneNumber;
          isVerified = false; // new numbers are never verified
          isNewPhone = true;
        }

        if (isNewPhone) {
          const { data: updateUserPhoneData } = await updateUserPhoneMutation({
            variables: {
              phone,
            },
          });
          if (!updateUserPhoneData?.collectUpdateUserPhone?.successful) {
            const { messages } =
              updateUserPhoneData?.collectUpdateUserPhone ?? {};
            if (messages?.length) {
              const errorMessage =
                messages[0]?.message ?? 'Failed to update phone';
              setError(errorMessage);
              tracker.track('Unified User Phone Update', {
                successful: false,
                error: errorMessage,
              });
              return;
            }
          }
          tracker.track('Unified User Phone Update', {
            successful: true,
          });
        } else {
          const { data: resolvePhoneConflictData } =
            await resolvePhoneConflictMutation({
              variables: {
                phone,
              },
            });
          if (
            !resolvePhoneConflictData?.collectResolvePhoneConflict?.successful
          ) {
            const { messages } =
              resolvePhoneConflictData?.collectResolvePhoneConflict ?? {};
            if (messages?.length) {
              const errorMessage =
                messages[0]?.message ?? 'Failed to resolve phone conflict';
              setError(errorMessage);
              tracker.track('Unified User Phone Conflict', {
                successful: false,
                error: errorMessage,
              });
              return;
            }
          }
          tracker.track('Unified User Phone Conflict', {
            successful: true,
          });
        }
      }

      // If the phone is already verified or we're just updating the name, we can refresh the data and close the modal
      if (isVerified || (shouldShowNameSection && !shouldShowPhoneSection)) {
        setIsRefetching(true);
        await client.refetchQueries({
          include: [
            'webGetUnifiedUserQuery',
            'webCurrentUserV2NewQuery',
            'webCurrentUserV2Query',
          ],
        });
        setIsRefetching(false);

        handleClose();
      } else {
        setPhoneToVerify(phone);
        setShowCollectVerifyUnifiedPhone(true);
      }
    } catch (e) {
      console.error('Form submission error:', e);
      setError('Something went wrong');
      tracker.track('Unified User Debug - Error', {
        error: JSON.stringify(e),
      });
    }
  };

  useEffect(() => {
    if (isModalOpen && issue) {
      tracker.track('Unified User Debug - Modal Opened', {
        issueType: issue.type,
        severity: issue.severity,
        phoneNumber: issue.phoneNumber,
      });
    }
  }, [isModalOpen, issue]);

  const { getPhoneConflictStatus } = useUnifiedUserStatus();
  const phoneConflictStatus = getPhoneConflictStatus();

  const defaultValues = useMemo(() => {
    let countryCode = 'US';
    if (unifiedUser?.phone?.number) {
      try {
        const parsed = parsePhoneNumber(unifiedUser.phone.number);
        countryCode = parsed.country || 'US';
      } catch {
        // Use default US if parsing fails
      }
    }
    return {
      firstName: unifiedUser?.firstName || '',
      lastName: unifiedUser?.lastName || '',
      selectedPhoneNumber: '',
      newPhoneNumber: '',
      countryCode,
    };
  }, [unifiedUser]);

  const methods = useForm<TUnifiedUserFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(UnifiedUserFormSchema),
    defaultValues: {
      ...defaultValues,
      selectedPhoneNumber: '',
    },
    shouldUnregister: true,
  });

  const selectedPhoneNumber = methods.watch('selectedPhoneNumber');
  const firstName = methods.watch('firstName');
  const lastName = methods.watch('lastName');

  const isNameValid =
    !shouldShowNameSection ||
    (firstName &&
      firstName.trim().length > 1 &&
      lastName &&
      lastName.trim().length > 1);

  const formatPhoneNumberDisplay = (phoneNumber: string) => {
    return formatPhoneNumber(phoneNumber);
  };

  const availablePhoneNumbers = useMemo(() => {
    const phones = [];

    if (phoneConflictStatus.livePhone?.number) {
      phones.push({
        id: 'live',
        number: phoneConflictStatus.livePhone.number,
        status: phoneConflictStatus.livePhone.status,
        source: 'Live',
      });
    }

    if (phoneConflictStatus.collectPhone?.number) {
      phones.push({
        id: 'collect',
        number: phoneConflictStatus.collectPhone.number,
        status: phoneConflictStatus.collectPhone.status,
        source: 'Collect',
      });
    }

    return phones;
  }, [phoneConflictStatus]);
  return (
    <ModalContent
      header={
        <>
          {showCollectVerifyUnifiedPhone && (
            <Button
              bg="transparent"
              border="none"
              padding="0"
              tabIndex={-1}
              width="fit-content"
              onClick={() => setShowCollectVerifyUnifiedPhone(false)}
            >
              <ChevronLeftIcon height="24px" width="24px" />
            </Button>
          )}
          {showCollectVerifyUnifiedPhone
            ? 'Verify phone number'
            : 'Update account'}
        </>
      }
      p="16px 24px 24px"
    >
      <Box>
        {showCollectVerifyUnifiedPhone && phoneToVerify ? (
          <CollectVerifyUnifiedPhone
            phone={phoneToVerify}
            onBack={handleClose}
          />
        ) : (
          <Form {...methods} onSubmit={handleSubmit}>
            {/* Name Section */}
            {shouldShowNameSection && (
              <Box>
                <Text
                  color={EColor.Black}
                  fontSize="16px"
                  fontWeight={500}
                  marginBottom="16px"
                >
                  What should we call you?
                </Text>
                <VStack spacing={3}>
                  <FormTextInput
                    autoComplete="given-name"
                    name="firstName"
                    placeholder="First Name"
                  />
                  <FormTextInput
                    autoComplete="family-name"
                    name="lastName"
                    placeholder="Last Name"
                  />
                </VStack>
              </Box>
            )}
            {/* Phone Number Section */}
            {shouldShowPhoneSection && (
              <VStack
                alignItems="flex-start"
                py="10px"
                spacing={5}
                width="100%"
              >
                <Text
                  color={EColor.Black}
                  fontSize="16px"
                  fontWeight={500}
                  marginBottom="16px"
                >
                  {phoneConflictStatus.hasConflict
                    ? 'Select your preferred phone number'
                    : 'Phone Number'}
                </Text>
                {phoneConflictStatus.hasConflict &&
                  availablePhoneNumbers.length > 0 && (
                    <VStack spacing={2} width="100%">
                      {availablePhoneNumbers.map((phone, idx) => (
                        <React.Fragment key={phone.id}>
                          <Flex
                            alignItems="center"
                            as="label"
                            gap={2}
                            htmlFor={phone.id}
                            width="100%"
                          >
                            <input
                              type="radio"
                              {...methods.register('selectedPhoneNumber', {
                                required: true,
                              })}
                              id={phone.id}
                              value={phone.number}
                            />
                            <Text color={EColor.Black}>
                              {formatPhoneNumberDisplay(phone.number)}
                            </Text>
                          </Flex>
                          {idx < availablePhoneNumbers.length - 1 && (
                            <Divider
                              backgroundColor="rgba(0, 0, 0, 0.05)"
                              border="none"
                              height="1px"
                              width="100%"
                            />
                          )}
                        </React.Fragment>
                      ))}
                      {availablePhoneNumbers.length > 0 && (
                        <Divider
                          backgroundColor="rgba(0, 0, 0, 0.05)"
                          border="none"
                          height="1px"
                          width="100%"
                        />
                      )}
                      <Flex
                        alignItems="center"
                        as="label"
                        gap={2}
                        htmlFor="new"
                        width="100%"
                      >
                        <input
                          type="radio"
                          {...methods.register('selectedPhoneNumber', {
                            required: true,
                          })}
                          id="new"
                          value="new"
                        />
                        <Text color={EColor.Black}>New phone number</Text>
                      </Flex>
                    </VStack>
                  )}
                {(selectedPhoneNumber === 'new' ||
                  !phoneConflictStatus.hasConflict) && (
                  <FormFloatingPhoneInput
                    countryCodeName="countryCode"
                    name="newPhoneNumber"
                    placeholder="Phone Number"
                  />
                )}
              </VStack>
            )}
            {error && (
              <Text color={EColor.Tomato} fontSize="14px">
                {error}
              </Text>
            )}
            <Button
              fontWeight={700}
              height="54px"
              isDisabled={
                !methods.formState.isValid || !isNameValid || isLoading
              }
              lineHeight="16px"
              mt="24px"
              type="submit"
              variant={EButtonVariant.BRAND_PRIMARY}
              width="100%"
            >
              {isLoading ? 'UPDATING...' : 'CONTINUE'}
            </Button>
          </Form>
        )}
      </Box>
    </ModalContent>
  );
};
